import { DuctedCoverageType } from 'utils/ducted-coverage';
import { DuctedOperatingCost } from 'utils/ducted-system-metrics';

function sumOrNull(values: Array<number | null | undefined>): number | null {
  if (values.some((value) => value == null)) return null;
  return values.reduce<number>((sum, value) => sum + (value as number), 0);
}

function weightedCoveragePercent(
  coverages: DuctedCoverageType[],
  percentKey: 'heatingCoveragePercent' | 'coolingCoveragePercent',
  requirementKey: 'heatingRequirementBtu' | 'coolingRequirementBtu'
): number | null {
  const requirementTotal = sumOrNull(
    coverages.map((coverage) => coverage[requirementKey])
  );
  const percents = coverages.map((coverage) => coverage[percentKey]);
  if (percents.some((percent) => percent == null)) return null;
  if (requirementTotal == null || requirementTotal <= 0) {
    return (
      percents.reduce<number>((sum, percent) => sum + (percent as number), 0) /
      percents.length
    );
  }
  const covered = coverages.reduce(
    (sum, coverage) =>
      sum +
      ((coverage[percentKey] as number) *
        (coverage[requirementKey] as number)) /
        100,
    0
  );
  return (covered / requirementTotal) * 100;
}

export function combineDuctedCoverage(
  coverages: Array<DuctedCoverageType | null | undefined>
): DuctedCoverageType | null {
  const present = coverages.filter(
    (coverage): coverage is DuctedCoverageType => coverage != null
  );
  if (present.length === 0 || present.length !== coverages.length) return null;
  if (present.length === 1) return present[0];
  return {
    basis: present[0].basis,
    heatingCoveragePercent: weightedCoveragePercent(
      present,
      'heatingCoveragePercent',
      'heatingRequirementBtu'
    ),
    coolingCoveragePercent: weightedCoveragePercent(
      present,
      'coolingCoveragePercent',
      'coolingRequirementBtu'
    ),
    heatingRequirementBtu: sumOrNull(
      present.map((coverage) => coverage.heatingRequirementBtu)
    ),
    coolingRequirementBtu: sumOrNull(
      present.map((coverage) => coverage.coolingRequirementBtu)
    )
  };
}

export function combineDuctedOperatingCost(
  costs: Array<DuctedOperatingCost | null | undefined>
): DuctedOperatingCost | null {
  const present = costs.filter(
    (cost): cost is DuctedOperatingCost => cost != null
  );
  if (present.length === 0 || present.length !== costs.length) return null;
  if (present.length === 1) return present[0];
  return {
    annualCoolingCost: sumOrNull(present.map((cost) => cost.annualCoolingCost)),
    annualHeatingCost: sumOrNull(present.map((cost) => cost.annualHeatingCost)),
    annualSupplementalHeatingCost: sumOrNull(
      present.map((cost) => cost.annualSupplementalHeatingCost)
    ),
    annualTotalCost: sumOrNull(present.map((cost) => cost.annualTotalCost)),
    coolingBasis: present[0].coolingBasis,
    heatingBasis: present[0].heatingBasis
  };
}
